import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Sparkles, GraduationCap, Building2, Shirt, Paintbrush, Gift, Flag, Printer, HelpCircle } from 'lucide-react';
import axios from 'axios';
import { useSmartSite, useSmartContent } from '../context/SmartSiteContext';
import { useIntentOrder, INTENT_LABELS } from '../hooks/useIntentOrder';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Intent quick-pick cards — each click fires a high-weight signal (cta_click=3pts)
 *  so we can rapidly identify intent for new visitors who otherwise wouldn't
 *  browse FAQs long enough to accumulate clicks. */
const INTENT_CHIPS = [
  { key: 'business-branding', label: 'Business / Team Branding', Icon: Building2, color: 'from-[#1B3A5C] to-[#2f587f]' },
  { key: 'school-uniforms', label: 'School Uniforms', Icon: Shirt, color: 'from-[#4A90A4] to-[#3a7183]' },
  { key: 'greek-apparel', label: 'Greek Apparel', Icon: Flag, color: 'from-[#6A0DAD] to-[#4f0a82]' },
  { key: 'graduations', label: 'Graduations', Icon: GraduationCap, color: 'from-[#F5A623] to-[#d88d13]' },
  { key: 'personalized-gifts', label: 'Personalized Gifts', Icon: Gift, color: 'from-[#C94F7C] to-[#a53d62]' },
  { key: 'custom-embroidery', label: 'Custom Embroidery', Icon: Paintbrush, color: 'from-[#3E7A3E] to-[#2e5c2e]' },
  { key: 'printing', label: 'Printing & Transfers', Icon: Printer, color: 'from-[#008080] to-[#006666]' },
];

/** Single FAQ accordion row (compact for 2-col grid). */
const FaqRow = ({ faq, index, isOpen, onToggle, showCategory }) => (
  <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm h-fit" data-testid={`faq-item-${index}`}>
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-4 p-5 text-left hover:text-[#4A90A4] transition-colors"
    >
      <span className="font-bold text-gray-900 text-sm sm:text-base pr-2 flex-1">{faq.question}</span>
      <div className="flex items-center gap-2 flex-shrink-0">
        {showCategory && faq.category && faq.category !== 'general' && (
          <span className="hidden md:inline-block text-[10px] uppercase tracking-wider font-semibold text-[#4A90A4]/70 bg-[#4A90A4]/5 px-2 py-1 rounded-full whitespace-nowrap">
            {INTENT_LABELS[faq.category] || faq.category}
          </span>
        )}
        <ChevronDown className={`w-5 h-5 text-[#4A90A4] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>
    </button>
    {isOpen && (
      <div className="px-5 pb-5 -mt-1">
        <p className="text-gray-500 leading-relaxed text-sm">{faq.answer}</p>
      </div>
    )}
  </div>
);

const FAQPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [openKey, setOpenKey] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null); // null = smart default
  const smart = useSmartSite();
  const heroData=useSmartContent('faq-hero','hero');
  const hero=heroData.items?.[0]?.data || {};
  const actionData=useSmartContent('faq-cta','cta');
  const action=actionData.items?.[0]?.data || {};
  const { topIntent } = useIntentOrder(faqs, 'category');

  useEffect(() => {
    axios.get(`${API_URL}/api/public/faqs`).then((r) => setFaqs(r.data)).catch(() => {});
  }, []);

  // All intent-bearing categories present in the data, in preferred order
  const categories = useMemo(() => {
    const seen = new Set();
    const order = [];
    for (const f of faqs) {
      if (f.category && f.category !== 'general' && !seen.has(f.category)) {
        seen.add(f.category);
        order.push(f.category);
      }
    }
    if (topIntent && order.includes(topIntent)) {
      return [topIntent, ...order.filter((c) => c !== topIntent)];
    }
    return order;
  }, [faqs, topIntent]);

  // Separate general FAQs (always shown at top)
  const generalFaqs = useMemo(() => faqs.filter((f) => f.category === 'general'), [faqs]);
  const intentFaqs = useMemo(() => faqs.filter((f) => f.category && f.category !== 'general'), [faqs]);

  // Group intent FAQs by category
  const grouped = useMemo(() => {
    const map = new Map();
    for (const f of intentFaqs) {
      if (!map.has(f.category)) map.set(f.category, []);
      map.get(f.category).push(f);
    }
    return map;
  }, [intentFaqs]);

  // Linear list when a category tab is clicked or topIntent is known
  const linearList = useMemo(() => {
    if (selectedCategory === 'all') return faqs;
    if (selectedCategory === 'general') return generalFaqs;
    if (selectedCategory) return faqs.filter((f) => f.category === selectedCategory);
    return [];
  }, [faqs, selectedCategory, generalFaqs]);

  const onCategoryClick = (cat) => {
    setSelectedCategory(cat);
    setOpenKey(null);
    window.scrollTo({ top: 240, behavior: 'smooth' });
    if (cat && cat !== 'all' && cat !== 'general') {
      smart?.track?.('cta_click', { content_category: cat, cta: 'faq_category_tab' });
    }
  };

  const onFaqOpen = (faq, key) => {
    const willOpen = openKey !== key;
    setOpenKey(willOpen ? key : null);
    if (willOpen && faq.category && faq.category !== 'general') {
      smart?.track?.('faq_click', { content_category: faq.category, item_id: faq.id });
    }
  };

  const showSmartBanner = topIntent && !selectedCategory;
  const showLinearView = selectedCategory !== null;

  return (
    <div className="min-h-screen bg-white" data-testid="faq-page">
      {/* Hero */}
      <section className="relative py-24 bg-[#4A90A4] overflow-hidden">
        <div className="absolute -top-20 -right-20 w-72 h-72 bg-white/5 rounded-full" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <nav className="text-sm text-white/50 mb-4">
            <Link to="/" className="hover:text-[#F5A623]">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-white">FAQ</span>
          </nav>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4 text-white">{hero.heading || 'Frequently Asked Questions'}</h1>
          <p className="text-white/75 max-w-2xl">
            {hero.body || "Browse by topic or scroll the full list. Tell us what you're kre8ing so we can show you the most relevant answers."}
          </p>
          {hero.image && <img src={hero.image} alt="" className="mt-6 max-h-64 max-w-full object-contain rounded-lg" />}
          {hero.cta_text && <Link to={hero.cta_link || '/shop'} className="inline-block mt-5 px-5 py-3 bg-[#F5A623] text-gray-900 rounded-full">{hero.cta_text}</Link>}
        </div>
      </section>

      {/* Intent chips — strong-signal quick picks */}
      <section className="bg-white border-b border-gray-100 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-xs uppercase tracking-[0.2em] text-gray-400 font-semibold mb-5">
            What are you kre8ing today?
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="intent-chips">
            {INTENT_CHIPS.filter(c => categories.includes(c.key)).map((chip) => {
              const active = selectedCategory === chip.key;
              return (
                <button
                  key={chip.key}
                  onClick={() => onCategoryClick(chip.key)}
                  data-testid={`intent-chip-${chip.key}`}
                  className={`group relative overflow-hidden rounded-xl p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-lg
                    ${active
                      ? `bg-gradient-to-br ${chip.color} text-white shadow-md`
                      : 'bg-white border border-gray-200 text-gray-700 hover:border-[#4A90A4]/40'}`}
                >
                  <chip.Icon className={`w-5 h-5 mb-2 ${active ? 'text-white' : 'text-[#4A90A4]'}`} />
                  <div className={`text-xs font-bold leading-tight ${active ? 'text-white' : 'text-gray-900'}`}>
                    {chip.label}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sticky category tabs */}
      {categories.length > 1 && (
        <section className="sticky top-0 z-20 bg-white border-b border-gray-100 shadow-sm">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-2 overflow-x-auto">
            <button
              onClick={() => onCategoryClick(null)}
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${
                !selectedCategory ? 'bg-[#1B3A5C] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="faq-tab-smart"
            >
              {topIntent ? `For You · ${INTENT_LABELS[topIntent] || topIntent}` : 'Overview'}
            </button>
            <button
              onClick={() => onCategoryClick('all')}
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${
                selectedCategory === 'all' ? 'bg-[#1B3A5C] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="faq-tab-all"
            >
              Everything
            </button>
            <button
              onClick={() => onCategoryClick('general')}
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${
                selectedCategory === 'general' ? 'bg-[#1B3A5C] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="faq-tab-general"
            >
              General
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => onCategoryClick(cat)}
                className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${
                  selectedCategory === cat ? 'bg-[#F5A623] text-gray-900' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`faq-tab-${cat}`}
              >
                {INTENT_LABELS[cat] || cat}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Smart banner */}
      {showSmartBanner && (
        <section className="bg-[#FDF6E9] border-b border-[#F5A623]/20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-3" data-testid="smart-faq-banner">
            <Sparkles className="w-5 h-5 text-[#F5A623] flex-shrink-0" />
            <p className="text-sm text-gray-700">
              We've prioritized <strong>{INTENT_LABELS[topIntent] || topIntent}</strong> questions based on your recent activity.{' '}
              <button onClick={() => onCategoryClick('all')} className="text-[#4A90A4] underline font-semibold">
                Show everything instead
              </button>
            </p>
          </div>
        </section>
      )}

      {/* Main content */}
      <section className="py-16 bg-[#FDF6E9]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {showLinearView ? (
            /* Filtered linear view — 2 columns */
            <>
              <div className="mb-6 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedCategory === 'all' && 'All Questions'}
                  {selectedCategory === 'general' && 'General Questions'}
                  {selectedCategory && selectedCategory !== 'all' && selectedCategory !== 'general' &&
                    (INTENT_LABELS[selectedCategory] || selectedCategory)}
                </h2>
                <button
                  onClick={() => onCategoryClick(null)}
                  className="text-sm text-[#4A90A4] hover:underline font-semibold"
                  data-testid="faq-back-to-overview"
                >
                  ← Back to overview
                </button>
              </div>
              <div className="grid md:grid-cols-2 gap-4" data-testid="faq-linear-view">
                {linearList.map((faq, i) => (
                  <FaqRow
                    key={faq.id}
                    faq={faq}
                    index={i}
                    isOpen={openKey === faq.id}
                    onToggle={() => onFaqOpen(faq, faq.id)}
                    showCategory={selectedCategory === 'all'}
                  />
                ))}
              </div>
            </>
          ) : (
            /* Default overview view: general first, then topIntent (if any), then other categories */
            <div className="space-y-14" data-testid="faq-overview-view">
              {/* General "Start here" block */}
              {generalFaqs.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-5">
                    <HelpCircle className="w-6 h-6 text-[#4A90A4]" />
                    <h2 className="text-2xl font-bold tracking-tight text-gray-900">Start Here</h2>
                    <span className="text-xs text-gray-400">Applies to every kre8ive</span>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    {generalFaqs.map((faq) => (
                      <FaqRow
                        key={faq.id}
                        faq={faq}
                        index={faq.id}
                        isOpen={openKey === faq.id}
                        onToggle={() => onFaqOpen(faq, faq.id)}
                        showCategory={false}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* topIntent promoted block */}
              {topIntent && grouped.has(topIntent) && (
                <div data-testid="faq-promoted-intent">
                  <div className="flex items-center gap-3 mb-5">
                    <Sparkles className="w-6 h-6 text-[#F5A623]" />
                    <h2 className="text-2xl font-bold tracking-tight text-gray-900">
                      {INTENT_LABELS[topIntent] || topIntent}
                    </h2>
                    <span className="text-xs text-[#F5A623] font-semibold uppercase tracking-wider">Top match for you</span>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    {grouped.get(topIntent).map((faq) => (
                      <FaqRow
                        key={faq.id}
                        faq={faq}
                        index={faq.id}
                        isOpen={openKey === faq.id}
                        onToggle={() => onFaqOpen(faq, faq.id)}
                        showCategory={false}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Remaining intent-bearing categories */}
              {categories.filter((c) => c !== topIntent).map((cat) => (
                <div key={cat}>
                  <div className="flex items-center gap-3 mb-5">
                    <h2 className="text-2xl font-bold tracking-tight text-gray-900">
                      {INTENT_LABELS[cat] || cat}
                    </h2>
                    <span className="text-xs text-gray-400">({(grouped.get(cat) || []).length})</span>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    {(grouped.get(cat) || []).map((faq) => (
                      <FaqRow
                        key={faq.id}
                        faq={faq}
                        index={faq.id}
                        isOpen={openKey === faq.id}
                        onToggle={() => onFaqOpen(faq, faq.id)}
                        showCategory={false}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Contact CTA */}
          <div className="mt-16 p-8 bg-white rounded-2xl shadow-sm border border-gray-100 text-center">
            <h3 className="text-xl font-bold mb-4 text-gray-900">{action.heading || 'Still have questions?'}</h3>
            <p className="text-gray-500 mb-6">{action.body || "We're here to help! Contact us for more information."}</p>
            <Link
              to={action.cta_link || "/contact"}
              className="inline-flex items-center px-6 py-3 bg-[#F5A623] text-gray-900 font-bold rounded-full btn-glow text-sm"
              data-testid="faq-contact-btn"
            >
              Contact Us <ChevronRight className="ml-1 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
